import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';

import {
  AxisScaleDirective,
  SupportedScale,
} from '../../directives/axis-scale.directive';
import { AxisDirective, Orientation } from '../../directives/axis.directive';
import { LegendDirective } from '../../directives/legend.directive';
import { ResizeObserverDirective } from '../../directives/resize-observer.directive';
import {
  BarAttributes,
  GroupedBarChartConfig,
  GroupedBarChartData,
  defaultConfig,
} from '../../model/chart.model';

@Component({
  selector: 'bsh-grouped-bar-chart',
  templateUrl: './grouped-bar-chart.component.html',
  styleUrls: ['./grouped-bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ResizeObserverDirective,
    AxisScaleDirective,
    AxisDirective,
    LegendDirective,
  ],
})
export class GroupedBarChartComponent {
  protected chartData: readonly GroupedBarChartData[] = [];
  protected config = { ...defaultConfig };
  protected renderMargin = { top: 20, right: 30, bottom: 40, left: 40 };
  protected width = 0;
  protected minSvgWidth = 0;
  protected height = 0;
  protected chartWidth = 0;
  protected chartHeight = 0;
  protected legendX = 0;
  protected legendY = 0;
  protected legendWidth = 120;
  protected legendSpacing = 20;
  protected groups: readonly (string | Date)[] = [];
  protected subgroups: readonly string[] = [];

  #yMax = 0;
  #xMax = 0;

  @Input() set currentChartData(data: readonly GroupedBarChartData[] | null) {
    if (data && data != this.chartData) {
      this.chartData = data;
      this.updateChart();
    }
  }
  @Input() set currentConfig(config: GroupedBarChartConfig) {
    this.config = { ...defaultConfig, ...config };
    this.legendWidth = this.config.showLegend
      ? (this.config.legendWidth ?? defaultConfig.legendWidth)
      : 0;
    this.minSvgWidth = this.config.minSvgWidth ?? defaultConfig.minSvgWidth;
    this.updateChart();
  }

  @ViewChild('mainScale', { static: true }) mainScale!: AxisScaleDirective;
  @ViewChild('valueScale', { static: true }) valueScale!: AxisScaleDirective;
  @ViewChild('subScale', { static: true }) subScale!: AxisScaleDirective;

  /**
   * Returns the range for the main axis scale based on chart orientation.
   * @returns {[number, number]} The range for the main axis scale.
   */
  get mainScaleRange(): [number, number] {
    return this.config.orientation === 'vertical'
      ? [0, this.chartWidth]
      : [0, this.chartHeight];
  }

  /**
   * Returns a formatting function for axis ticks.
   * @returns {(d: string | number | Date) => string} The tick formatting function.
   */
  get tickFormat(): (d: string | number | Date) => string {
    return d => (d instanceof Date ? d3.timeFormat('%a %d')(d) : d + '');
  }

  /**
   * Returns the range for the value axis scale based on chart orientation.
   * @returns {[number, number]} The range for the value axis scale.
   */
  get valueScaleRange(): [number, number] {
    return this.config.orientation === 'vertical'
      ? [this.chartHeight, 0]
      : [0, this.chartWidth];
  }

  /**
   * Returns the domain for the value axis scale based on chart orientation.
   * @returns {[number, number]} The domain for the value axis scale.
   */
  get valueScaleDomain(): [number, number] {
    return this.config.orientation === 'vertical'
      ? [0, this.#yMax]
      : [0, this.#xMax];
  }

  /**
   * Determines if subgroup labels should be rotated based on chart width and config.
   * @returns {boolean} True if subgroup labels should be rotated.
   */
  protected shouldRotateSubgroupLabels(): boolean {
    return (
      this.chartWidth <
        this.groups.length * this.subgroups.length * this.subgroupLabelLength &&
      this.config.showSubgroupLabels
    );
  }

  /**
   * Calculates the approximate length of subgroup labels.
   * @returns {number} The length in pixels.
   */
  get subgroupLabelLength(): number {
    // Approximate length based on subgroup names
    return this.subgroups.length > 0
      ? this.subgroups.reduce((a, b) => (a.length > b.length ? a : b)).length *
          7
      : 0;
  }

  /**
   * Calculates the approximate length of group labels.
   * @returns {number} The length in pixels.
   */
  protected groupLabelLength(): number {
    return this.groups.length > 0
      ? (
          this.groups.reduce((a, b) =>
            a instanceof Date && b instanceof Date
              ? a
              : (a as string).length > (b as string).length
                ? a
                : b
          ) as string
        ).length * 7
      : 0;
  }

  /**
   * Determines if group labels should be rotated based on chart width.
   * @returns {boolean} True if group labels should be rotated.
   */
  protected shouldRotateGroupLabels(): boolean {
    return (
      this.groups.length > 0 &&
      this.chartWidth < this.groups.length * this.groupLabelLength()
    );
  }

  protected get hideZeroValues(): boolean {
    return this.config.hideZeroValues;
  }

  /**
   * Returns the visible subgroups for a given data item, filtering out zero values if configured.
   * @param {GroupedBarChartData} d The data item.
   * @returns {string[]} Array of visible subgroup keys.
   */
  protected getVisibleSubgroups(d: GroupedBarChartData): string[] {
    return this.hideZeroValues
      ? this.subgroups.filter(key => +d[key] != 0)
      : (this.subgroups as string[]);
  }

  /**
   * Returns the SVG transform for a bar group based on orientation and group position.
   * @param {GroupedBarChartData} data The data item.
   * @returns {string} The SVG transform string.
   */
  protected barTransform(data: GroupedBarChartData): string {
    return this.config.orientation === 'vertical'
      ? 'translate(' + this.mainScale.position(data.group) + ',0)'
      : 'translate(0,' + this.mainScale.position(data.group) + ')';
  }

  /**
   * Calculates label attributes for a subgroup label.
   * @param {string} key The subgroup key.
   * @returns {{x: number, y: number, anchor: 'start' | 'middle' | 'end'}} The label attributes.
   */
  protected subgroupLabelAttributes(key: string): {
    x: number;
    y: number;
    anchor: 'start' | 'middle' | 'end';
  } {
    if (this.config.orientation === 'vertical') {
      const x = this.subScale.position(key) + this.subScale.bandwidth / 2;
      return {
        x: x,
        y: 0,
        anchor: this.shouldRotateSubgroupLabels() ? 'end' : 'middle',
      };
    } else {
      return {
        x: 0,
        y: this.subScale.position(key) + this.subScale.bandwidth / 2 + 4,
        anchor: 'end',
      };
    }
  }

  /**
   * Returns the SVG transform for a subgroup label.
   * @param {number} x The x position.
   * @param {number} y The y position.
   * @returns {string} The SVG transform string.
   */
  protected subgroupLabelTransform(x: number, y: number): string {
    if (this.config.orientation === 'vertical') {
      if (this.shouldRotateSubgroupLabels()) {
        return `translate(${x},${y}) rotate(-65)`;
      }
    }
    return `translate(${x},${y})`;
  }

  /**
   * Returns the SVG transform for a group label.
   * @param {string | Date} group The group value.
   * @returns {string} The SVG transform string.
   */
  protected groupLabelTransform(group: string | Date): string {
    const x =
      this.config.orientation === 'vertical'
        ? this.mainScale.position(group)
        : -5;
    const y =
      this.config.orientation === 'vertical'
        ? this.chartHeight + 20
        : this.mainScale.position(group) || 0;
    return `translate(${x},${y})`;
  }

  /**
   * Calculates the SVG attributes for a grid line.
   * @param {number | Date} tick The tick value.
   * @returns {{x1: number, y1: number, x2: number, y2: number}} The grid line attributes.
   */
  protected gridLineAttributes(tick: number | Date): {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } {
    return this.config.orientation === 'vertical'
      ? {
          x1: 0,
          x2: this.chartWidth,
          y1: this.valueScale.position(tick),
          y2: this.valueScale.position(tick),
        }
      : {
          x1: this.valueScale.position(tick),
          x2: this.valueScale.position(tick),
          y1: 0,
          y2: this.chartHeight,
        };
  }

  /**
   * Calculates the SVG attributes for a bar.
   * @param {string} key The subgroup key.
   * @param {GroupedBarChartData} data The data item.
   * @returns {BarAttributes} The bar attributes.
   */
  protected barAttributes(
    key: string,
    data: GroupedBarChartData
  ): BarAttributes {
    const visibleSubgroups = this.getVisibleSubgroups(data);
    const index = visibleSubgroups.indexOf(key);
    const count = visibleSubgroups.length;
    const groupBand = this.mainScale.bandwidth;
    const padding = 0.01; // 1% padding between bars
    const totalPadding = (count - 1) * padding * groupBand;
    const localBandwidth = count > 0 ? (groupBand - totalPadding) / count : 0;

    const barOffset = index * (localBandwidth + padding * groupBand);
    if (this.config.orientation === 'vertical') {
      return {
        x: barOffset,
        y: this.valueScale.position(data[key]),
        width: localBandwidth,
        height: this.chartHeight - this.valueScale.position(data[key]),
        fill: this.config.colors[
          this.subgroups.indexOf(key) % this.config.colors.length
        ],
        valueX: barOffset + localBandwidth / 2,
        valueY: this.valueScale.position(data[key]) - 5,
        anchor: 'middle',
      };
    } else {
      return {
        x: 0,
        y: barOffset,
        width: this.valueScale.position(data[key]),
        height: localBandwidth,
        fill: this.config.colors[
          this.subgroups.indexOf(key) % this.config.colors.length
        ],
        valueX: this.valueScale.position(data[key]) + 4,
        valueY: barOffset + localBandwidth / 2 + 4,
        anchor: 'start',
      };
    }
  }

  /**
   * Returns the scale for a given axis position.
   * @param {Orientation} position The axis position.
   * @returns {SupportedScale} The scale object.
   */
  protected scale(position: Orientation): SupportedScale {
    if (position === 'bottom' || position === 'top') {
      return this.config.orientation === 'vertical'
        ? this.mainScale.scale
        : this.valueScale.scale;
    } else {
      return this.config.orientation === 'vertical'
        ? this.valueScale.scale
        : this.mainScale.scale;
    }
  }

  /**
   * Determines if axis data should be hidden for a given position.
   * @param {Orientation} position The axis position.
   * @returns {boolean} True if axis data should be hidden.
   */
  protected isAxisDataHidden(position: Orientation): boolean {
    if (position === 'bottom' || position === 'top') {
      // Hide group labels when vertical and config is set to show subgroup labels
      return this.config.orientation === 'vertical'
        ? this.config.showSubgroupLabels
        : false;
    } else {
      return this.config.orientation === 'horizontal'
        ? this.config.showSubgroupLabels
        : false;
    }
  }

  /**
   * Handles resize events and updates chart dimensions.
   * @param {DOMRectReadOnly} evt The resize event.
   */
  protected observeResize(evt: DOMRectReadOnly) {
    this.width = Math.max(evt.width, this.minSvgWidth);
    this.height = evt.height;
    this.updateChart();
  }

  /**
   * Updates chart dimensions, groups, subgroups, and scales based on current data and config.
   * Ensures that chart is rendered correctly.
   * Key steps include:
   * 1. Validating that data and dimensions are available.
   * 2. Adjusting chart margins based on orientation and configuration.
   * 3. Calculating the inner chart area size, legend position, and spacing.
   * 4. Extracting and sorting subgroups and groups from the data.
   * 5. Computing the maximum value for the value axis, depending on orientation.
   * 6. Dynamically setting the chart height to accommodate rotated labels if needed.
   * Called internally when data or config changes.
   * @private
   */
  private updateChart() {
    if (!this.chartData?.length || this.width === 0 || this.height === 0)
      return;
    const margin = { ...this.config.margin };
    if (this.config.orientation === 'horizontal') {
      margin.left = Math.max(margin.left, 80); // when horizontal, ensure enough space for group labels
    }
    this.renderMargin = margin; // Store adjusted margin

    const { left, right, top, bottom } = margin;
    this.chartWidth = Math.max(
      this.width - left - right - this.legendWidth - this.legendSpacing,
      0
    );
    this.legendX = this.width - this.legendWidth - right;
    this.legendY = top;
    const dataSortedByKeys = Array.from(this.chartData).sort((a, b) => {
      return Object.keys(b).length - Object.keys(a).length;
    });
    this.subgroups = Object.keys(dataSortedByKeys[0]).filter(
      k => k !== 'group'
    );

    this.groups = this.chartData.map(d => d.group);

    if (this.config.orientation === 'vertical') {
      this.#yMax =
        d3.max(this.chartData, d => d3.max(this.subgroups, key => +d[key])) ||
        0;
    } else {
      this.#xMax =
        d3.max(this.chartData, d => d3.max(this.subgroups, key => +d[key])) ||
        0;
    }
    // height calculated after groups and subgroups are set to ensure correct dimensions
    this.chartHeight = Math.max(
      this.height -
        top -
        bottom -
        (this.shouldRotateSubgroupLabels()
          ? this.subgroupLabelLength
          : this.shouldRotateGroupLabels()
            ? this.groupLabelLength()
            : 20),
      0
    );
  }
}
